import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * Without one, an uncaught render error unmounts the entire React tree and leaves the
 * visitor with a blank white page — no navigation, no explanation, no way to recover
 * except to guess that reloading might help.
 *
 * Still a class component because React provides no hook equivalent:
 * `componentDidCatch` and `getDerivedStateFromError` have no functional counterpart. This
 * is the one place in the codebase where a class is the correct answer rather than a
 * legacy one.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /**
     * The single place a client-side crash is reported.
     *
     * `console.error` keeps it visible in development and in a user's browser console.
     * Wiring Sentry (or similar) here is a one-line addition and the reason this hook
     * exists as a named seam rather than being inlined.
     */

    console.error('Unhandled render error:', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="grid min-h-dvh place-items-center bg-surface px-6">
        <div className="flex max-w-lg flex-col items-start gap-5">
          <p className="text-eyebrow font-bold uppercase tracking-[0.1em] text-primary-strong">
            Something went wrong
          </p>
          <h1 className="text-[2rem] font-semibold leading-tight text-heading">
            This page did not load correctly
          </h1>
          <p className="text-body">
            The problem has been logged. Reloading usually clears it — if it does not, please let us
            know what you were doing.
          </p>

          {/*
            The technical detail is shown in development only. In production it would
            leak internals to a visitor while telling them nothing they can act on.
          */}
          {import.meta.env.DEV ? (
            <pre className="max-h-64 w-full overflow-auto rounded-input bg-surface-wash p-4 text-xs text-error">
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          ) : null}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-full bg-gradient-to-r from-primary-strong to-primary px-6 py-3 text-sm font-semibold text-white"
            >
              Reload the page
            </button>
            {/*
              A plain anchor, not a router Link: the router may be the thing that broke,
              so a full document navigation is the reliable escape.
            */}
            <a
              href="/"
              className="rounded-full border border-outline-strong px-6 py-3 text-sm font-semibold text-heading"
            >
              Go to home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
